import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, gbp, openFile } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Save, ArrowLeft, RefreshCw, Calendar, ClipboardList } from "lucide-react";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "../components/ui/sonner";

// Quote Builder building blocks
import {
  initialsOf, sortLabourTypes, tmpId,
} from "../components/quote-builder/utils";
import { SummaryGroup, SummaryRow } from "../components/quote-builder/layout";
import { MoneyLineTable } from "../components/quote-builder/MoneyLineTable";
import { SundryLineTable } from "../components/quote-builder/SundryLineTable";
import { SimilarQuoteBanner } from "../components/quote-builder/SimilarQuoteBanner";
import { MaterialsTable } from "../components/quote-builder/MaterialsTable";
import { LabourTable } from "../components/quote-builder/LabourTable";
import { CustomerJobForm } from "../components/quote-builder/CustomerJobForm";
import { SaveConfirmDialog } from "../components/quote-builder/SaveConfirmDialog";
import { AccessHireTable } from "../components/quote-builder/AccessHireTable";
import { HotelTable } from "../components/quote-builder/HotelTable";
import { useSupplierCheck } from "../components/quote-builder/useSupplierCheck";

const DEFAULTS = {
  customer_name: "",
  customer_id: null,
  site_name: "",
  site_address: "",
  customer_surcharge: 0,
  labour_only: false,
  quote_number: "",
  category: "",
  job_type: "Install",
  description: "",
  branch: "",
  estimator: "",
  postcode: "",
  visits: 1,
  markup_percent: 35,
  sundry_percent: 0,
  callout_fee: 0,
  engineers: 1,
  hotel_cost_per_night: 0,
  nights: 0,
  food_per_day: 0,
  travel_hours_override: 0,
  job_days: 1,
  extras: [],
  sundries: [],
  callouts: [],
  access_hire: [],
  repair_parts: [],
  line_items: [],
  labour_items: [],
  status: "Draft",
};

export default function QuoteBuilderPage() {
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Managers share admin's ability to override the markup floor (below 35%)
  // and the auto-hours floor on labour rows, but their other privileges stay
  // at the per-user level. Pass `isAdmin || isManager` into MarkupCell.
  const canOverrideFloors = isAdmin || user?.role === "manager";
  const userInitials = useMemo(() => initialsOf(user), [user]);

  const [quote, setQuote] = useState(DEFAULTS);
  const supplierCheck = useSupplierCheck({ category: quote.category, setQuote, user });
  // Travel-triggered hotel prompt: when the estimator enters a one-way travel
  // time that crosses the configured threshold (default 2hrs / 4hr round-trip),
  // we ask once whether a hotel is needed. The answer flips `quote.hotel.required`
  // and is remembered for the session so we don't nag.
  const [hotelPromptShown, setHotelPromptShown] = useState(false);
  const [branches, setBranches] = useState([]);
  const [postcodes, setPostcodes] = useState([]);
  const [products, setProducts] = useState([]);
  const [quoteCategories, setQuoteCategories] = useState([]);
  const [repairParts, setRepairParts] = useState([]);
  const [markupOptions, setMarkupOptions] = useState([]);
  const [minMarkup, setMinMarkup] = useState(null);
  const [ahProducts, setAhProducts] = useState([]);
  const [fieldRequirements, setFieldRequirements] = useState({});
  const [preview, setPreview] = useState(null);
  const [costSettings, setCostSettings] = useState({ cost_label: "Cost", sell_label: "Sell price to client", labour_cost_rate: 45 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [descBusy, setDescBusy] = useState(false);
  const [descError, setDescError] = useState("");
  const [descCopied, setDescCopied] = useState(false);
  const [similarQuotes, setSimilarQuotes] = useState([]);
  const [similarDismissed, setSimilarDismissed] = useState(false);
  // Draft attachments uploaded BEFORE the quote was saved.
  // Merged into the quote's attachments array on POST /api/quotes via pending_attachments.
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const confirm = useConfirm();
  // Multi-day prompt — fires once when the total hours first cross the configured
  // max working day. Tracked across re-renders so the dialog isn't re-fired on
  // every key-stroke.
  const multiDayPromptedRef = useRef(false);
  // Once the estimator has acknowledged "yes, this is a multi-day job", the
  // number of days is derived automatically (no manual entry) using the built-
  // in rule: `days = ceil(site_labour_hours / max_working_day_hours)`. Travel
  // hours are excluded because they themselves scale with `job_days`.
  const [multiDayConfirmed, setMultiDayConfirmed] = useState(false);

  // Compute current total billable hours (site labour + travel). Used by the
  // multi-day prompt and the summary chip.
  const totalHours = useMemo(() => {
    return (quote.labour_items || []).reduce(
      (sum, lb) => sum + Number(lb.hours || 0) * (Number(lb.multiplier) || 1),
      0,
    );
  }, [quote.labour_items]);

  // Site-only labour hours (no travel rows). Drives the built-in days rule.
  const siteHours = useMemo(() => {
    return (quote.labour_items || [])
      .filter((lb) => !lb._auto_travel && lb.work_type !== "Travel")
      .reduce((sum, lb) => sum + Number(lb.hours || 0) * (Number(lb.multiplier) || 1), 0);
  }, [quote.labour_items]);

  // Multi-day prompt: as soon as total hours exceed the configured max-working-day,
  // ask the user whether the job spans multiple days (Yes / No). No manual day
  // entry — once they say Yes, `multiDayConfirmed` is flipped on and the days
  // auto-derive from the labour rows via the effect below.
  useEffect(() => {
    const max = Number(costSettings.max_working_day_hours || 12);
    if (max <= 0) return;
    if (multiDayPromptedRef.current) return;
    if ((quote.labour_items || []).length === 0) return;
    if (multiDayConfirmed) return; // already opted in
    if (totalHours <= max) return;
    multiDayPromptedRef.current = true;
    (async () => {
      const isMultiDay = await confirm({
        title: `Total hours have exceeded ${max} per day`,
        description: `This quote currently has ${totalHours.toFixed(2)} hours of labour & travel — past the maximum working day of ${max} hours. Is this job spread over more than one day?`,
        confirmText: "Yes, multi-day job",
        cancelText: "No, single day",
      });
      if (!isMultiDay) return;
      setMultiDayConfirmed(true);
      toast.success("Multi-day mode on — days auto-derived from labour hours.");
    })();
  }, [totalHours, costSettings.max_working_day_hours, quote.labour_items, multiDayConfirmed, confirm]);

  // Built-in rule: once multi-day is confirmed, derive `job_days` from site
  // labour hours: `ceil(site_hours / max_working_day_hours)`. Recomputes live
  // as labour rows change. Single day if site hours drop ≤ max.
  useEffect(() => {
    if (!multiDayConfirmed) return;
    const max = Number(costSettings.max_working_day_hours || 12);
    if (max <= 0) return;
    const derived = Math.max(1, Math.ceil(siteHours / max));
    setQuote((q) => (Number(q.job_days || 1) === derived ? q : { ...q, job_days: derived }));
  }, [multiDayConfirmed, siteHours, costSettings.max_working_day_hours]);

  // Hotel prompt — when the live preview's round-trip travel hours imply
  // the one-way trip is ≥ the configured threshold (default 2hrs), nudge
  // the estimator to confirm whether an overnight stay is needed. We use
  // `hotelPromptShown` so we only ask once per quote-builder session; the
  // estimator can still flip the toggle manually any time afterwards.
  //
  // Travel is now driven by an `_auto_travel` labour row (round-trip hours
  // = postcode_one_way × 2 × job_days). preview.travel_hours stays 0 unless
  // there's a manual override, so we derive one-way hours from the labour
  // row directly. Per-day basis = round_trip / 2 / job_days.
  useEffect(() => {
    if (hotelPromptShown) return;
    if (quote.hotel?.required) return; // already opted in
    const days = Math.max(1, Number(quote.job_days || 1));
    const autoTravel = (quote.labour_items || []).find((lb) => lb._auto_travel);
    const autoRoundTrip = autoTravel ? Number(autoTravel.hours || 0) : 0;
    const fromOverride = Number(quote.travel_hours_override || 0);
    const roundTripHours = autoRoundTrip > 0 ? autoRoundTrip : fromOverride;
    const oneWayHrs = (roundTripHours / 2) / days;
    const threshold = Number(costSettings.hotel_trigger_one_way_hours ?? 2);
    if (oneWayHrs < threshold) return;
    setHotelPromptShown(true);
    (async () => {
      const yes = await confirm({
        title: `One-way travel is ${oneWayHrs.toFixed(1)} hours — is a hotel needed?`,
        description: "Engineers travelling this far typically stay overnight. If yes, we'll add the Hotel section (and collapse multi-day travel to a single round trip). You can flip this any time from the Hotel section toggle.",
        confirmText: "Yes, hotel required",
        cancelText: "No, day trip",
      });
      if (yes) {
        setQuote((q) => ({ ...q, hotel: { ...(q.hotel || {}), required: true, items: (q.hotel?.items?.length ? q.hotel.items : []) } }));
        toast.success("Hotel section enabled.");
      }
    })();
  }, [quote.labour_items, quote.travel_hours_override, quote.job_days, costSettings.hotel_trigger_one_way_hours, quote.hotel, hotelPromptShown, confirm]);


  // Look up past quotes in the same category whenever the category changes
  // Only show similar quotes for Repair jobs — Install jobs don't reuse pricing.
  useEffect(() => {
    if (!quote.category || quote.job_type !== "Repair") { setSimilarQuotes([]); return; }
    setSimilarDismissed(false);
    // Clear immediately so stale matches don't flash while the new request is in
    // flight (e.g. user picks a category → 5 cat-matches load → user adds a
    // part → backend filters and returns 0; without this reset the banner
    // briefly shows the 5 cat-matches before the empty response arrives).
    setSimilarQuotes([]);
    const parts = (quote.repair_parts || []).join(",");
    const url = `/quotes/similar?category=${encodeURIComponent(quote.category)}${parts ? `&parts=${encodeURIComponent(parts)}` : ""}${isEdit ? `&exclude_id=${id}` : ""}`;
    let cancelled = false;
    api
      .get(url)
      .then((r) => { if (!cancelled) setSimilarQuotes(r.data.matches || []); })
      .catch(() => { if (!cancelled) setSimilarQuotes([]); });
    return () => { cancelled = true; };
  }, [quote.category, quote.job_type, quote.repair_parts, isEdit, id]);

  // Fetch repair parts — filter by category if one's picked, otherwise show all
  useEffect(() => {
    const url = quote.category
      ? `/repair-parts?category=${encodeURIComponent(quote.category)}`
      : "/repair-parts";
    api.get(url)
      .then((r) => setRepairParts(r.data || []))
      .catch(() => setRepairParts([]));
  }, [quote.category]);

  useEffect(() => {
    Promise.all([
      api.get("/branches"),
      api.get("/postcodes"),
      api.get("/products"),
      api.get("/quote-categories"),
      api.get("/markup-options"),
      api.get("/field-requirements"),
      api.get("/markup-options/min").catch(() => ({ data: { min_percent: null } })),
      api.get("/access-hire-products").catch(() => ({ data: [] })),
      api.get("/cost-settings").catch(() => ({ data: { cost_label: "Cost", sell_label: "Sell price to client", labour_cost_rate: 45 } })),
    ]).then(([b, p, pr, qc, mk, fr, mm, ah, cs]) => {
      setBranches(b.data);
      setPostcodes(p.data);
      setProducts(pr.data);
      setQuoteCategories(qc.data);
      setMarkupOptions(mk.data || []);
      setFieldRequirements(fr.data?.required || {});
      setMinMarkup(mm.data?.min_percent ?? null);
      setAhProducts(ah.data || []);
      setCostSettings(cs.data || { cost_label: "Cost", sell_label: "Sell price to client", labour_cost_rate: 45 });
    });
    if (isEdit) {
      api.get(`/quotes/${id}`).then((r) => {
        const d = r.data;
        setQuote({
          ...DEFAULTS,
          ...d,
          line_items: (d.line_items || []).map((x) => ({ id: x.id || tmpId(), ...x })),
          labour_items: (d.labour_items || []).map((x) => ({ id: x.id || tmpId(), ...x })),
          access_hire: (d.access_hire || []).map((x) => ({ ...x, id: x.id || tmpId() })),
        });
        // Loaded quote was saved as multi-day → keep auto-derivation on so any
        // edits to labour rows stay in sync with the built-in days rule.
        if (Number(d.job_days || 1) > 1) {
          setMultiDayConfirmed(true);
          multiDayPromptedRef.current = true;
        }
      });
    } else if (user?.name) {
      setQuote((q) => (q.estimator ? q : { ...q, estimator: user.name }));
    }
  }, [id, isEdit, user]);

  // Live preview (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/quotes/preview", quote).then((r) => setPreview(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [quote]);

  const update = (patch) => setQuote((q) => ({ ...q, ...patch }));

  // Auto-create / update a Travel labour row from the selected postcode.
  // Stores `_auto_travel: true` so we know which row to replace. Travel hours
  // are roundtrip × job_days (1 day = one round-trip, 2 days = two round-trips, …).
  //
  // EXCEPTION: when a hotel is required, engineers stay on site between days
  // so there's only ONE round trip for the whole job — we collapse the day
  // multiplier to 1 to match the backend's `visits = 1` carve-out.
  useEffect(() => {
    if (postcodes.length === 0) return;
    const code = (quote.postcode || "").toUpperCase().trim();
    const pc = code ? postcodes.find((p) => p.code === code) : null;
    const rawDays = Math.max(1, Number(quote.job_days || 1));
    const hotelRequired = !!quote.hotel?.required;
    const days = hotelRequired ? 1 : rawDays;
    const roundTripHours = pc ? Number(pc.travel_time_hours || 0) * 2 * days : 0;

    setQuote((q) => {
      const others = (q.labour_items || []).filter((lb) => !lb._auto_travel);
      if (!pc || roundTripHours <= 0) {
        if (others.length === q.labour_items.length) return q;
        return { ...q, labour_items: others };
      }
      const existing = (q.labour_items || []).find((lb) => lb._auto_travel);
      const travelRow = {
        id: existing?.id || tmpId(),
        product_name: existing?.product_name || "",
        custom_product: existing?.custom_product || "",
        work_type: "Travel",
        hours: roundTripHours,
        difficulty: "Standard",
        branch: q.branch || existing?.branch || "",
        _auto_travel: true,
      };
      return { ...q, labour_items: [travelRow, ...others] };
    });
  }, [quote.postcode, quote.branch, quote.job_days, quote.hotel?.required, postcodes]);

  // ---- AI Supplier PDF upload (extract -> append a materials line) ----
  const handlePdfFile = async (f) => {
    if (!f) return;
    setUploading(true);
    setError("");
    setUploadMsg(`Reading ${f.name} with Claude…`);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/quotes/extract-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const li = { ...data.line_item, id: tmpId() };
      setQuote((q) => ({ ...q, line_items: [...q.line_items, li] }));
      supplierCheck.check({ section: "line_items", lineId: li.id, name: li.supplier_name || li.product_name, source: "drag" });
      const br = data.extracted || {};
      const ocrTag = data.used_vision_fallback ? " · (OCR — please double-check)" : "";
      setUploadMsg(
        `✓ Added line · ${data.line_item.supplier_name || "Supplier"} · ${
          data.line_item.supplier_quote_ref || "no ref"
        } · NET £${data.line_item.supplier_cost.toFixed(2)} (goods £${br.subtotal ?? 0} + delivery £${br.delivery ?? 0}, VAT £${br.tax ?? 0} excluded)${ocrTag}`
      );
      setTimeout(() => setUploadMsg(""), 9000);
    } catch (ex) {
      setUploadMsg("");
      setError(ex.response?.data?.detail?.toString() || ex.message || "Failed to extract PDF");
    } finally {
      setUploading(false);
    }
  };

  // ---- AI Supplier IMAGE upload (extract -> append a materials line) ----
  // Uses Claude vision. Fires the same materials row pipeline as the PDF
  // path. supplier_name is left blank when the image has no legible letter-
  // head so the estimator can type it in.
  const handleMaterialsImageFile = async (f) => {
    if (!f) return;
    setUploading(true);
    setError("");
    setUploadMsg(`Reading ${f.name} with Claude vision…`);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/quotes/extract-materials-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const li = { ...data.row, id: tmpId() };
      setQuote((q) => ({ ...q, line_items: [...q.line_items, li] }));
      supplierCheck.check({ section: "line_items", lineId: li.id, name: li.supplier_name || li.product_name, source: "drag" });
      const ex_ = data.extracted || {};
      setUploadMsg(
        `✓ Added line from image · ${li.product_name || "(supplier blank — fill in)"} · cost £${Number(li.supplier_cost).toFixed(2)}${ex_.supplier_quote_ref ? ` · ref ${ex_.supplier_quote_ref}` : ""}`
      );
      setTimeout(() => setUploadMsg(""), 9000);
    } catch (ex) {
      setUploadMsg("");
      setError(ex.response?.data?.detail?.toString() || ex.message || "Failed to extract image");
    } finally {
      setUploading(false);
    }
  };

  // ---- AI Access Hire PDF upload (extract -> append an access_hire row) ----
  const [ahPdfUploading, setAhPdfUploading] = useState(false);
  const [ahPdfMsg, setAhPdfMsg] = useState("");
  const [ahPdfError, setAhPdfError] = useState("");
  const handleAccessHirePdfFile = async (f) => {
    if (!f) return;
    setAhPdfUploading(true);
    setAhPdfError("");
    setAhPdfMsg(`Reading ${f.name} with Claude…`);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/quotes/extract-access-hire-pdf", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const row = { ...data.row, id: tmpId() };
      setQuote((q) => ({ ...q, access_hire: [...(q.access_hire || []), row] }));
      supplierCheck.check({ section: "access_hire", lineId: row.id, name: row.supplier_name, source: "drag" });
      const br = data.extracted || {};
      setAhPdfMsg(
        `✓ Added hire row · ${row.product}${row.type ? " · " + row.type : ""} · ${row.days}d × ${row.quantity} @ £${Number(row.supplier_cost).toFixed(2)}/day (total £${br.total ?? "—"})`
      );
      setTimeout(() => setAhPdfMsg(""), 9000);
    } catch (ex) {
      setAhPdfMsg("");
      setAhPdfError(ex.response?.data?.detail?.toString() || ex.message || "Failed to extract hire PDF");
    } finally {
      setAhPdfUploading(false);
    }
  };

  // ---- AI description generation ----
  const generateDescription = async (style = "default") => {
    setDescBusy(true);
    setDescError("");
    setDescCopied(false);
    try {
      const { data } = await api.post(
        `/quotes/suggest-description${style && style !== "default" ? `?style=${style}` : ""}`,
        quote
      );
      if (data?.description) update({ description: data.description });
    } catch (ex) {
      setDescError(ex.response?.data?.detail?.toString() || ex.message || "Failed to generate description");
    } finally {
      setDescBusy(false);
    }
  };

  const copyDescription = async () => {
    if (!quote.description) return;
    try {
      await navigator.clipboard.writeText(quote.description);
      setDescCopied(true);
      setTimeout(() => setDescCopied(false), 2500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = quote.description;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setDescCopied(true);
      setTimeout(() => setDescCopied(false), 2500);
    }
  };

  // ---- Required-field validation ----
  const requiredErrors = () => {
    const fieldValues = {
      customer_name: (quote.customer_name || "").trim(),
      quote_number: (quote.quote_number || "").trim(),
      job_type: (quote.job_type || "").trim(),
      category: (quote.category || "").trim(),
      estimator: (quote.estimator || "").trim(),
      branch: (quote.branch || "").trim(),
      postcode: (quote.postcode || "").trim(),
      description: (quote.description || "").trim(),
      repair_parts: (quote.repair_parts && quote.repair_parts.length > 0) ? "y" : "",
    };
    const labels = {
      customer_name: "Customer Name", quote_number: "Quote Number", job_type: "Job Type",
      category: "Category", estimator: "Estimator", branch: "Labour Type",
      postcode: "Postcode", description: "Brief description", repair_parts: "Parts required",
    };
    const missing = [];
    Object.entries(fieldRequirements).forEach(([k, required]) => {
      if (!required) return;
      if (k === "repair_parts" && (quote.job_type !== "Repair" || quote.labour_only)) return;
      if (!fieldValues[k]) missing.push(labels[k] || k);
    });
    return missing;
  };

  const save = async () => {
    const missing = requiredErrors();
    if (missing.length) {
      setError(`Please fill in: ${missing.join(", ")}`);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const body = {
        ...quote,
        pending_attachments: pendingAttachments.map((a) => a.id),
      };
      const r = isEdit
        ? await api.put(`/quotes/${id}`, body)
        : await api.post("/quotes", body);
      setPendingAttachments([]);
      nav(`/quotes/${r.data.id}`);
    } catch (ex) {
      setError(ex.response?.data?.detail?.toString() || ex.message);
    } finally {
      setBusy(false);
    }
  };

  // Door-category products feed the labour Product Type dropdown. Filter is
  // case-insensitive + accepts the common variants ("Door" / "Doors" / "door")
  // because production data has historically been edited by hand. Falls back
  // to showing ALL products if no door-category match exists at all, so the
  // dropdown is never empty when products do exist (Jun 2026 prod regression
  // — see QUO/10231-style reports).
  const doorProducts = useMemo(() => {
    const normalised = (s) => (s || "").toString().trim().toLowerCase().replace(/s$/, "");
    const doors = products.filter((p) => normalised(p.category) === "door");
    return doors.length > 0 ? doors : products;
  }, [products]);
  const labourTypes = useMemo(() => sortLabourTypes(branches), [branches]);
  // Persist each section's collapsed state per quote (or "new" for unsaved drafts)
  const sectionKey = (name) => `qb:${id || "new"}:${name}`;
  const SECTION_NAMES = ["customer", "materials", "labour", "access-hire", "sundries", "callouts", "extras"];
  // Bumping this remounts every Section so each re-reads its persisted state.
  const [sectionsVersion, setSectionsVersion] = useState(0);
  const setAllSections = (open) => {
    SECTION_NAMES.forEach((n) => {
      try { window.localStorage.setItem(sectionKey(n), open ? "1" : "0"); } catch { /* ignore */ }
    });
    setSectionsVersion((v) => v + 1);
  };

  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          onClick={() => nav(-1)}
          className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
          data-testid="back-button"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="hidden sm:inline-flex h-9 border border-slate-300 rounded-full overflow-hidden text-[10px] uppercase tracking-wider font-semibold" data-testid="collapse-controls">
            <button
              onClick={() => setAllSections(false)}
              className="px-3 hover:bg-slate-100 text-slate-600 hover:text-slate-900 border-r border-slate-300"
              data-testid="collapse-all-button"
              title="Collapse every section"
            >
              Collapse all
            </button>
            <button
              onClick={() => setAllSections(true)}
              className="px-3 hover:bg-slate-100 text-slate-600 hover:text-slate-900"
              data-testid="expand-all-button"
              title="Expand every section"
            >
              Expand all
            </button>
          </div>
          {error && <span className="text-red-600 text-sm" data-testid="quote-error">{error}</span>}
          {quote.survey_pdf_file_id && (
            <a
              href="#" onClick={(e) => { e.preventDefault(); openFile(quote.survey_pdf_file_id, id); }}
              data-testid="download-survey-pdf"
              className="h-10 px-4 border border-brand-blue text-brand-blue text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue hover:text-white"
              title="Download the original survey PDF so you can share it with a supplier"
            >
              <ClipboardList size={14} /> Download survey PDF
            </a>
          )}
          <button
            onClick={() => setShowSaveConfirm(true)}
            disabled={busy}
            data-testid="save-quote-button"
            className="h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 hover:bg-brand-blue-dark disabled:opacity-50"
          >
            <Save size={14} /> {busy ? "Saving…" : isEdit ? "Update Quote" : "Save Quote"}
          </button>
        </div>
      </div>

      {supplierCheck.elements}
      <SaveConfirmDialog
        open={showSaveConfirm}
        onCancel={() => setShowSaveConfirm(false)}
        onConfirm={() => { setShowSaveConfirm(false); save(); }}
        isEdit={isEdit}
      />

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-0 xl:gap-6">
        <div className="xl:col-span-3 space-y-6" key={`sections-${sectionsVersion}`}>
          {!similarDismissed && similarQuotes.length > 0 && (
            quote.job_type !== "Repair" || (quote.repair_parts || []).length > 0
          ) && (
            <SimilarQuoteBanner
              category={quote.category}
              partsCount={(quote.repair_parts || []).length}
              matches={similarQuotes}
              onDismiss={() => setSimilarDismissed(true)}
            />
          )}

          <CustomerJobForm
            quote={quote}
            setQuote={setQuote}
            branches={branches}
            labourTypes={labourTypes}
            postcodes={postcodes}
            quoteCategories={quoteCategories}
            onShowSuppliers={supplierCheck.enabled ? supplierCheck.openPanel : null}
            repairParts={repairParts}
            setRepairParts={setRepairParts}
            fieldRequirements={fieldRequirements}
            isAdmin={isAdmin}
            costSettings={costSettings}
            descBusy={descBusy}
            descError={descError}
            descCopied={descCopied}
            generateDescription={generateDescription}
            copyDescription={copyDescription}
            storageKey={sectionKey("customer")}
          />

          <div className="qb-card px-4 py-3 flex items-center gap-3" data-testid="labour-only-toggle">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!quote.labour_only}
                onChange={(e) => update({
                  labour_only: e.target.checked,
                  // Purge any lingering material lines when switching to
                  // labour-only so the totals don't include phantom costs.
                  ...(e.target.checked ? { line_items: [] } : {}),
                })}
                className="h-4 w-4"
                data-testid="labour-only-checkbox"
              />
              <span className="text-sm font-semibold text-slate-900">Labour Only quote</span>
              <span className="text-xs text-slate-500 hidden sm:inline">
                — tick this when no parts / materials are required (e.g. service, callout, adjustment).
              </span>
            </label>
          </div>

          {!quote.labour_only && (
            <MaterialsTable
              items={quote.line_items}
              onChange={(next) => update({ line_items: next })}
              markupOptions={markupOptions}
              userInitials={userInitials}
              defaultMarkup={quote.markup_percent}
              isAdmin={canOverrideFloors}
              minMarkup={quote.markup_percent}
              uploading={uploading}
              uploadMsg={uploadMsg}
              onPdfFile={handlePdfFile}
              onImageFile={handleMaterialsImageFile}
              quoteId={id}
              supplierChecksOn={supplierCheck.enabled}
              onSupplierBlur={(li, name) => supplierCheck.check({ section: "line_items", lineId: li.id, name, source: "typed", rec: li.unapproved_supplier })}
              storageKey={sectionKey("materials")}
            />
          )}

          {quote.job_days > 1 && (
            <div className="qb-card border-l-4 border-brand-blue bg-blue-50/40 px-4 py-3 flex items-center gap-3 fade-up" data-testid="multi-day-banner">
              <Calendar size={16} className="text-brand-blue shrink-0" />
              <div className="flex-1 text-sm">
                <span className="font-semibold text-slate-900">Multi-day job — {quote.job_days} days on site.</span>
                <span className="text-slate-600 ml-1">
                  Auto-derived from {siteHours.toFixed(1)}h of site labour ÷ {Number(costSettings.max_working_day_hours || 12)}h max/day. Travel × {quote.job_days} (one round-trip per day).
                </span>
              </div>
              <button
                onClick={() => {
                  multiDayPromptedRef.current = false;
                  setMultiDayConfirmed(false);
                  update({ job_days: 1 });
                  toast.success("Reverted to single day.");
                }}
                className="text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-red-600"
                data-testid="multi-day-clear"
              >Revert to single day</button>
            </div>
          )}

          <LabourTable
            items={quote.labour_items}
            onChange={(next) => update({ labour_items: next })}
            branches={branches}
            labourTypes={labourTypes}
            doorProducts={doorProducts}
            quoteBranch={quote.branch}
            jobType={quote.job_type}
            storageKey={sectionKey("labour")}
            costSettings={costSettings}
            canAddHours={user?.role === "admin" || user?.role === "manager" || user?.can_add_hours !== false}
            canSubtractHours={user?.role === "admin" || user?.role === "manager" || user?.can_subtract_hours !== false}
            canOverrideHoursFloor={user?.role === "admin" || user?.role === "manager"}
            userInitials={userInitials}
          />

          <AccessHireTable
            items={quote.access_hire || []}
            onChange={(next) => update({ access_hire: next })}
            ahProducts={ahProducts}
            markupOptions={markupOptions}
            userInitials={userInitials}
            defaultMarkup={quote.markup_percent}
            isAdmin={canOverrideFloors}
            minMarkup={quote.markup_percent}
            previewItems={preview?.access_hire}
            previewTotal={preview?.access_hire_total || 0}
            storageKey={sectionKey("access-hire")}
            quoteId={isEdit ? id : null}
            pendingAttachments={pendingAttachments}
            setPendingAttachments={setPendingAttachments}
            onPdfFile={handleAccessHirePdfFile}
            supplierChecksOn={supplierCheck.enabled}
            onSupplierBlur={(ln, name) => supplierCheck.check({ section: "access_hire", lineId: ln.id, name, source: "typed", rec: ln.unapproved_supplier })}
            pdfUploading={ahPdfUploading}
            pdfMsg={ahPdfMsg}
            pdfError={ahPdfError}
          />

          <HotelTable
            hotel={quote.hotel || {}}
            engineers={Number(quote.engineers || 1)}
            costSettings={costSettings}
            onChange={(next) => update({ hotel: next })}
            previewItems={preview?.hotel?.items}
            previewAddedCosts={preview?.hotel?.added_costs}
            previewTotal={preview?.hotel?.section_total || 0}
            storageKey={sectionKey("hotel")}
          />

          <SundryLineTable
            items={quote.sundries || []}
            onChange={(next) => update({ sundries: next })}
            tmpId={tmpId}
            previewItems={preview?.sundries}
            previewTotal={preview?.sundry_cost || 0}
            defaultMarkup={quote.markup_percent}
            markupOptions={markupOptions}
            userInitials={userInitials}
            isAdmin={canOverrideFloors}
            minMarkup={quote.markup_percent}
            storageKey={sectionKey("sundries")}
          />

          <MoneyLineTable
            title="Callouts"
            subtitle="Site-visit callout charges — add a line per visit / engineer"
            testIdPrefix="callout"
            items={quote.callouts || []}
            onChange={(next) => update({ callouts: next })}
            placeholder="e.g. Emergency callout - out of hours"
            tmpId={tmpId}
            totalLabel="Callouts total"
            previewTotal={preview?.callout_fee || 0}
            storageKey={sectionKey("callouts")}
          />

          <MoneyLineTable
            title="Extras"
            subtitle="One-off charges (skip hire, lifting equipment, sub-contractors…)"
            testIdPrefix="extra"
            items={quote.extras || []}
            onChange={(next) => update({ extras: next })}
            placeholder="e.g. Skip hire (1 week)"
            tmpId={tmpId}
            totalLabel="Extras total"
            previewTotal={preview?.extras_total || 0}
            storageKey={sectionKey("extras")}
            withMarkup
            defaultMarkup={quote.markup_percent || 35}
          />
        </div>

        {/* Live calc sidebar */}
        <aside className="xl:col-span-1">
          <div className="sticky top-6 qb-card overflow-hidden fade-up">
            <div className="h-1 bg-brand-orange" />
            <div className="px-5 py-4 flex items-center justify-between bg-gradient-to-br from-brand-blue to-brand-blue-dark text-white">
              <div className="text-xs uppercase tracking-[0.2em] font-bold">Live Calculation</div>
              <RefreshCw size={12} className="opacity-60" />
            </div>
            <div className="p-4 space-y-3 text-sm">
              <SummaryGroup title="Materials (with markup)">
                {preview && preview.category_totals_with_markup &&
                  Object.entries(preview.category_totals_with_markup).map(([cat, v]) => (
                    <SummaryRow key={cat} k={cat} v={gbp(v)} />
                  ))}
                <SummaryRow strong k="Subtotal" v={gbp(preview?.materials_total || 0)} />
              </SummaryGroup>

              <SummaryGroup title="Labour">
                {(() => {
                  const labour = preview?.labour_items || [];
                  const travel = labour.filter((l) => l.work_type === "Travel");
                  const site = labour.filter((l) => l.work_type !== "Travel");
                  const travelHrs = travel.reduce((s, l) => s + Number(l.hours || 0), 0);
                  const travelCost = travel.reduce((s, l) => s + Number(l.line_cost || 0), 0);
                  const siteHrs = site.reduce((s, l) => s + Number(l.hours || 0), 0);
                  const siteCost = site.reduce((s, l) => s + Number(l.line_cost || 0), 0);
                  return (
                    <>
                      {travel.length > 0 && (
                        <SummaryRow
                          k={`Travel labour (${travelHrs}h)`}
                          v={gbp(travelCost)}
                          testid="summary-travel-labour"
                        />
                      )}
                      <SummaryRow
                        k={`Site labour (${siteHrs}h)`}
                        v={gbp(siteCost)}
                        testid="summary-site-labour"
                      />
                      <SummaryRow strong k="Labour total" v={gbp(preview?.labour_subtotal || 0)} testid="summary-labour" />
                    </>
                  );
                })()}
              </SummaryGroup>

              {(preview?.customer_surcharge || quote.customer_surcharge) > 0 && (
                <SummaryGroup title="Client Surcharge">
                  <SummaryRow
                    strong
                    k={`Surcharge${quote.customer_name ? ` · ${quote.customer_name}` : ""}`}
                    v={gbp(preview?.customer_surcharge ?? quote.customer_surcharge ?? 0)}
                    testid="summary-customer-surcharge"
                  />
                </SummaryGroup>
              )}

              <SummaryGroup title="Extras">
                <SummaryRow k="Sundries" v={gbp(preview?.sundry_cost || 0)} />
                <SummaryRow k="Access hire" v={gbp(preview?.access_hire_total || 0)} testid="summary-access-hire" />
                {(preview?.hotel?.section_total || 0) > 0 && (
                  <SummaryRow k="Hotel" v={gbp(preview?.hotel?.section_total || 0)} testid="summary-hotel" />
                )}
                <SummaryRow k="Callouts" v={gbp(preview?.callout_fee || 0)} />
                <SummaryRow k="One-off extras" v={gbp(preview?.extras_total || 0)} />
                <SummaryRow
                  k={`Contingency Sundry${preview?.contingency_sundry_percent ? ` (${preview.contingency_sundry_percent}%)` : ""}`}
                  v={gbp(preview?.contingency_sundry_amount || 0)}
                  testid="summary-contingency-sundry"
                />
                <SummaryRow
                  k={`Administration Charge${preview?.administration_percent ? ` (${preview.administration_percent}%)` : ""}`}
                  v={gbp(preview?.administration_amount || 0)}
                  testid="summary-administration"
                />
              </SummaryGroup>

              <div className="mt-2 rounded-xl bg-slate-100 border-2 border-slate-200 px-4 py-3" data-testid="cost-block">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-600 font-bold mb-1" data-testid="cost-label">{costSettings.cost_label || "Cost"}</div>
                <div className="font-mono text-2xl font-black tracking-tight text-slate-800" data-testid="total-cost">
                  {gbp(preview?.total_cost || 0)}
                </div>
              </div>

              <div className="mt-2 rounded-xl bg-gradient-to-br from-slate-900 to-brand-blue-dark text-white px-4 py-3 shadow-md">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-orange font-bold mb-1" data-testid="sell-label">{costSettings.sell_label || "Sell price to client"}</div>
                <div className="font-mono text-3xl font-black tracking-tight text-white" data-testid="grand-total">
                  {gbp(preview?.grand_total || 0)}
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
